import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Credits() {
  const { user, refresh } = useAuth();
  const [bal, setBal] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [history, setHistory] = useState([]);
  const [claiming, setClaiming] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const [referral, setReferral] = useState(null);
  const [applyCode, setApplyCode] = useState("");

  const loadAll = async () => {
    if (!user) return;
    try {
      const [b, p, h, r] = await Promise.all([
        api.get("/credits/balance"),
        api.get("/credits/pricing"),
        api.get("/credits/history"),
        api.get("/referral/code"),
      ]);
      setBal(b.data);
      setPricing(p.data);
      setHistory(h.data.items || []);
      setReferral(r.data);
    } catch (e) {
      /* handled by auth guard */
    }
  };
  useEffect(() => { loadAll(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const claimReward = async (source) => {
    setClaiming(source);
    try {
      const { data } = await api.post("/credits/reward", { source });
      toast.success(`+${data.granted} credits · ${source}`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reward failed");
    } finally { setClaiming(null); }
  };

  const startCheckout = async (plan_id, pack_id) => {
    const key = plan_id || pack_id;
    setCheckoutBusy(key);
    try {
      const { data } = await api.post("/credits/checkout", {
        plan_id, pack_id, origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Checkout URL missing");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally {
      setCheckoutBusy(null);
    }
  };

  const applyReferral = async () => {
    if (!applyCode.trim()) { toast.error("Enter a referral code"); return; }
    try {
      const { data } = await api.post("/referral/apply", { code: applyCode.trim() });
      toast.success(`+${data.granted_to_you} credits · code ${data.code} applied`);
      setApplyCode("");
      loadAll();
      refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Referral failed");
    }
  };

  const copyReferral = () => {
    if (!referral?.code) return;
    const url = `${window.location.origin}?ref=${referral.code}`;
    navigator.clipboard?.writeText(url);
    toast.success("Referral link copied");
  };

  if (!user) return (
    <div className="p-8 text-center" data-testid="credits-signin-required">
      <div className="font-serif-elite text-3xl mb-2">Credits &amp; Billing</div>
      <div className="text-[color:var(--text-secondary)] text-sm">Sign in to view your credit balance and unlock plans.</div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6" data-testid="credits-page">
      <div className="border-b border-[color:var(--border-default)] pb-4">
        <div className="text-[10px] font-mono-data text-[#FFD700] tracking-widest mb-2">◆ CREDITS &amp; BILLING</div>
        <h1 className="font-serif-elite text-4xl md:text-5xl font-bold tracking-tight">Your Credit Economy</h1>
      </div>

      {bal && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[color:var(--border-default)]" data-testid="balance-summary">
          <Cell label="BALANCE" value={bal.balance} tone="gold" />
          <Cell label="FREE REMAINING" value={`${bal.free_remaining} / 100`} tone="bull" />
          <Cell label="PURCHASED" value={bal.purchased} />
          <Cell label="LIFETIME USED" value={bal.lifetime_used} />
        </div>
      )}

      {/* Referral */}
      {referral && (
        <section data-testid="referral-panel">
          <SectionTitle title="REFER &amp; EARN" subtitle={`+${referral.referrer_reward} to you · +${referral.referee_reward} to them per signup`} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[color:var(--border-default)]">
            <div className="p-4 border-r border-[color:var(--border-default)]">
              <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">YOUR CODE</div>
              <div className="font-mono-data text-3xl text-gold mt-1" data-testid="my-referral-code">{referral.code}</div>
              <div className="text-[11px] font-mono-data text-[color:var(--text-secondary)] mt-1">
                {referral.referred_count} friend{referral.referred_count === 1 ? "" : "s"} signed up · {referral.total_earned} credits earned
              </div>
              <button
                data-testid="copy-referral-btn"
                onClick={copyReferral}
                className="mt-3 text-[10px] font-mono-data tracking-widest px-3 py-1.5 border border-[color:var(--border-default)] text-white hover:bg-[color:var(--bg-hover)] transition-colors"
              >COPY INVITE LINK</button>
            </div>
            <div className="p-4">
              <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">HAVE A CODE?</div>
              <div className="flex gap-2 mt-2">
                <input
                  data-testid="referral-input"
                  value={applyCode}
                  onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  disabled={!!user?.referred_by}
                  className="flex-1 bg-[color:var(--bg-surface)] border border-[color:var(--border-default)] px-2 py-1.5 text-sm font-mono-data text-white placeholder:text-[color:var(--text-muted)] focus:outline-none focus:border-[color:var(--border-focus)] disabled:opacity-40"
                />
                <button
                  data-testid="apply-referral-btn"
                  onClick={applyReferral}
                  disabled={!applyCode.trim() || !!user?.referred_by}
                  className="px-3 py-1.5 bg-white text-black text-[11px] font-mono-data tracking-widest hover:opacity-90 disabled:opacity-40 transition-colors"
                >APPLY</button>
              </div>
              <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-2">
                {user?.referred_by ? `Code ${user.referred_by} already applied.` : `One-time use. Gets you +${referral.referee_reward} credits.`}
              </div>
            </div>
          </div>
        </section>
      )}

      <section>
        <SectionTitle title="EARN CREDITS" subtitle="Watch an ad, log in daily, share a report" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <RewardCard label="WATCH AD" reward="+5" cap="cap 100/day" onClick={() => claimReward("ad")} loading={claiming === "ad"} />
          <RewardCard label="DAILY LOGIN" reward="+5" cap="cap 50/day" onClick={() => claimReward("daily_login")} loading={claiming === "daily_login"} />
          <RewardCard label="SHARE REPORT" reward="+2" cap="cap 20/day" onClick={() => claimReward("share")} loading={claiming === "share"} />
        </div>
      </section>

      {pricing && (
        <section>
          <SectionTitle title="PLANS" subtitle="Monthly credit allowance + Pro features" />
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-0 border border-[color:var(--border-default)]">
            {pricing.plans.map(p => (
              <div key={p.id} className="p-4 border-r border-b border-[color:var(--border-default)]" data-testid={`plan-${p.id}`}>
                <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">{p.name.toUpperCase()}</div>
                <div className="font-mono-data text-2xl text-white mt-1">{p.price_usd === null ? "Contact" : `$${p.price_usd}`}</div>
                <div className="text-[10px] font-mono-data text-[color:var(--text-secondary)] mt-1">
                  {p.credits_month === null ? "Unlimited" : `${p.credits_month.toLocaleString()} credits/mo`}
                </div>
                <ul className="mt-2 space-y-0.5">
                  {p.features.slice(0, 4).map((f) => <li key={f} className="text-[10px] font-body text-[color:var(--text-secondary)]">→ {f}</li>)}
                </ul>
                <button
                  data-testid={`checkout-${p.id}`}
                  onClick={() => p.id !== "free" && p.price_usd !== null && startCheckout(p.id, null)}
                  disabled={p.id === "free" || p.price_usd === null || checkoutBusy === p.id}
                  className="w-full mt-3 py-1.5 text-[10px] font-mono-data tracking-widest border border-[color:var(--border-default)] text-white hover:bg-[color:var(--bg-hover)] disabled:opacity-40 transition-colors"
                >{p.id === "free" ? "CURRENT" : p.price_usd === null ? "CONTACT" : checkoutBusy === p.id ? "…" : "UPGRADE →"}</button>
              </div>
            ))}
          </div>
        </section>
      )}

      {pricing && (
        <section>
          <SectionTitle title="CREDIT PACKS" subtitle="One-time top-up · powered by Stripe" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[color:var(--border-default)]">
            {pricing.packs.map(p => (
              <div key={p.id} className="p-4 border-r border-b border-[color:var(--border-default)] text-center" data-testid={`pack-${p.id}`}>
                <div className="font-mono-data text-xl text-white">{p.credits.toLocaleString()}</div>
                <div className="text-[10px] font-mono-data text-[color:var(--text-muted)]">CREDITS</div>
                <div className="font-mono-data text-lg text-gold mt-2">${p.price_usd}</div>
                <button
                  data-testid={`checkout-${p.id}`}
                  onClick={() => startCheckout(null, p.id)}
                  disabled={checkoutBusy === p.id}
                  className="w-full mt-2 py-1.5 text-[10px] font-mono-data tracking-widest bg-white text-black hover:opacity-90 disabled:opacity-40 transition-colors"
                >{checkoutBusy === p.id ? "…" : "BUY"}</button>
              </div>
            ))}
          </div>
        </section>
      )}

      {pricing && (
        <section>
          <SectionTitle title="PRICING PER ACTION" subtitle="What each feature costs" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[color:var(--border-default)]">
            {Object.entries(pricing.actions).filter(([, c]) => c > 0).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between p-3 border-r border-b border-[color:var(--border-default)]">
                <span className="text-[11px] font-mono-data text-[color:var(--text-secondary)]">{k.replace(/_/g, " ")}</span>
                <span className="font-mono-data text-sm text-gold">{v}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <SectionTitle title="HISTORY" subtitle="Last 50 credit events" />
        <div className="border border-[color:var(--border-default)]">
          {history.length === 0 ? (
            <div className="p-4 text-xs text-[color:var(--text-muted)] font-mono-data">No credit activity yet.</div>
          ) : history.map((h) => (
            <div key={`${h.ts}-${h.action}-${h.cost}`} className="flex items-center justify-between px-3 py-1.5 border-b border-[color:var(--border-default)]">
              <span className="text-[11px] font-mono-data text-[color:var(--text-secondary)]">{h.action}</span>
              <span className={`text-[11px] font-mono-data ${h.cost > 0 ? "text-bear" : "text-bull"}`}>{h.cost > 0 ? `-${h.cost}` : `+${-h.cost}`}</span>
              <span className="text-[10px] font-mono-data text-[color:var(--text-muted)]">{(h.ts || "").slice(0, 19).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Cell({ label, value, tone }) {
  const c = tone === "gold" ? "text-gold" : tone === "bull" ? "text-bull" : "text-white";
  return (
    <div className="p-4 border-r border-b border-[color:var(--border-default)]">
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
      <div className={`font-mono-data text-2xl mt-1 ${c}`}>{value}</div>
    </div>
  );
}

function SectionTitle({ title, subtitle }) {
  return (
    <div className="flex items-baseline gap-3 mb-2">
      <h2 className="text-[11px] font-mono-data tracking-widest text-white">{title}</h2>
      <span className="text-[10px] font-body text-[color:var(--text-muted)]">{subtitle}</span>
    </div>
  );
}

function RewardCard({ label, reward, cap, onClick, loading }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="border border-[color:var(--border-default)] p-4 text-left hover:bg-[color:var(--bg-elevated)] transition-colors disabled:opacity-50"
      data-testid={`reward-${label.toLowerCase().replace(/ /g, "-")}`}
    >
      <div className="text-[10px] font-mono-data text-[color:var(--text-muted)]">{label}</div>
      <div className="font-mono-data text-3xl text-bull mt-1">{loading ? "…" : reward}</div>
      <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-1">{cap}</div>
    </button>
  );
}
